package com.tregz.tremenz

import androidx.compose.animation.core.animateDpAsState
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.NavigationBar
import androidx.compose.material3.NavigationBarItem
import androidx.compose.material3.NavigationBarItemDefaults
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Snackbar
import androidx.compose.material3.SnackbarDuration
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.SnackbarResult
import androidx.compose.material3.Text
import androidx.compose.material3.CenterAlignedTopAppBar
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.material3.minimumInteractiveComponentSize
import androidx.compose.foundation.layout.Row
import com.tregz.design.motion.MotionSpring
import com.tregz.design.motion.isReducedMotion
import com.tregz.design.window.WindowProvider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.movableContentOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.backhandler.BackHandler
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import com.tregz.tremenz.io.audio.rememberAudioRouter
import com.tregz.tremenz.io.model.ModelData
import com.tregz.tremenz.io.notify.NotifyPlayback
import com.tregz.tremenz.io.player.PlayerMedia
import com.tregz.tremenz.ui.component.ContainerPlayer
import com.tregz.tremenz.ui.icon.IconKey
import com.tregz.tremenz.ui.icon.iconPainter
import com.tregz.tremenz.ui.label.LabelKey
import com.tregz.tremenz.ui.label.labelText
import com.tregz.tremenz.ui.media.LocalPlayerOverlayTop
import com.tregz.tremenz.ui.media.MediaCast
import com.tregz.tremenz.ui.route.RouteKey
import com.tregz.tremenz.ui.route.routeSet
import com.tregz.tremenz.ui.screen.ScreenChannels
import com.tregz.tremenz.ui.screen.ScreenSite
import com.tregz.tremenz.ui.screen.ScreenLibrary
import com.tregz.tremenz.ui.theme.LocalThemeSwitch
import com.tregz.tremenz.ui.screen.ScreenTracks
import com.tregz.tremenz.ui.display.isImmersiveLandscape
import com.tregz.tremenz.ui.display.isLandscape
import com.tregz.tremenz.ui.area.isTabConstrained
import com.tregz.tremenz.ui.area.nextTabExpanded
import com.tregz.tremenz.ui.area.playerHeight
import com.tregz.tremenz.ui.area.tabContentHeight
import com.tregz.tremenz.ui.area.tabTopPadding

/**
 * The app shell, shared by every target: the scaffold, the navigation bar, the persistent player
 * and the app wide dialogs.
 *
 * Which destinations appear is the one thing that differs per platform, and that lives behind
 * [routeSet] rather than in here — Web offers Mixes, Edits and Channels, Android Mixes only. The
 * host supplies playback state and [onOpenLink]; nothing platform specific leaks in.
 */
@OptIn(ExperimentalMaterial3Api::class, ExperimentalComposeUiApi::class)
@Composable
fun App(
    currentMedia: ModelData.Media?,
    player: PlayerMedia,
    isPreparingVideo: Boolean,
    isVideoPlaying: Boolean,
    onVideoPlayToggle: (Boolean) -> Unit,
    isPreparingSong: Boolean = false,
    isSongPlaying: Boolean = false,
    onSongPlayToggle: (Boolean) -> Unit = {},
    onSongStateChange: (Int) -> Unit = {},
    onSongError: (Int) -> Unit = {},
    onSongEnding: () -> Unit = {},
    onSongAdDetected: () -> Unit = {},
    isContinuousMix: Boolean = true,
    onContinuousMixToggle: (Boolean) -> Unit = {},
    fsNotification: NotifyPlayback?,
    hasAcceptedTerms: Boolean,
    onAcceptTerms: () -> Unit,
    // Cancels playback; the player offers it while a prepare is still running.
    onStopPlayback: () -> Unit = {},
    onPlayMedia: (ModelData.Media) -> Unit,
    onOpenLink: (String) -> Unit = {},
    routes: List<RouteKey> = routeSet()
) {
    val scheme = MaterialTheme.colorScheme
    val themeSwitch = LocalThemeSwitch.current
    val router = rememberAudioRouter()

    // The route is saved by name: an enum is not something every target's saved state can hold.
    // An empty route set falls back to the first key rather than crashing.
    var routeName by rememberSaveable(routes) {
        mutableStateOf((routes.firstOrNull() ?: RouteKey.entries.first()).name)
    }
    val route = routes.firstOrNull { it.name == routeName } ?: routes.firstOrNull() ?: RouteKey.entries.first()
    var termsAccepted by remember(hasAcceptedTerms) { mutableStateOf(hasAcceptedTerms) }
    var showOutputDialog by rememberSaveable { mutableStateOf(false) }
    var showShareDialog by rememberSaveable { mutableStateOf(false) }
    var isInfoOpen by rememberSaveable { mutableStateOf(false) }

    val clipboardManager = LocalClipboardManager.current
    val scope = rememberCoroutineScope()

    // Whether the content area is too short for a tab to be readable under the player, and whether
    // the tab is currently lifted over it. Measured in the scaffold body, read here because the
    // navigation bar is the other place a tab click can toggle the overlay.
    var canExpandTabs by remember { mutableStateOf(true) }
    var isTabExpanded by rememberSaveable { mutableStateOf(false) }

    // Back closes the Info panel or collapses the expanded tab before it leaves the app.
    BackHandler(enabled = isInfoOpen || isTabExpanded) {
        if (isInfoOpen) isInfoOpen = false else isTabExpanded = false
    }

    val snackbarHostState = remember { SnackbarHostState() }
    // Read through State so a language change updates the text without restarting the snackbar.
    val termsMessage by rememberUpdatedState(labelText(LabelKey.TERMS_MESSAGE))
    val agreeLabel by rememberUpdatedState(labelText(LabelKey.ACTION_AGREE))

    LaunchedEffect(termsAccepted) {
        // Loops because showTransient dismisses whatever is on screen, the terms included.
        while (!termsAccepted) {
            val result = snackbarHostState.showSnackbar(
                message = termsMessage,
                actionLabel = agreeLabel,
                duration = SnackbarDuration.Indefinite
            )
            if (result == SnackbarResult.ActionPerformed) {
                termsAccepted = true
                onAcceptTerms()
            }
        }
    }

    // A short notice replaces the current snackbar instead of queueing behind the Indefinite one.
    val showTransient: (String) -> Unit = { message ->
        scope.launch {
            snackbarHostState.currentSnackbarData?.dismiss()
            snackbarHostState.showSnackbar(message)
        }
    }

    val unavailableMessage = labelText(LabelKey.PLAYER_UNAVAILABLE)
    val onSongErrorShown: (Int) -> Unit = { code ->
        showTransient(unavailableMessage)
        onSongError(code)
    }

    // Asked again when the dialog opens or the media changes, so a device connected since is listed.
    val outputs = remember(router, currentMedia, showOutputDialog) {
        runCatching { router.outputs() }.getOrDefault(emptyList())
    }
    if (showOutputDialog) {
        AlertDialog(
            onDismissRequest = { showOutputDialog = false },
            title = { Text(labelText(LabelKey.OUTPUT_TITLE)) },
            text = {
                LazyColumn(modifier = Modifier.fillMaxWidth()) {
                    items(outputs) { device ->
                        Text(
                            text = "${device.name} - ${device.type}",
                            modifier = Modifier
                                .fillMaxWidth()
                                .minimumInteractiveComponentSize()
                                .clickable {
                                    runCatching { router.select(device) }
                                    showOutputDialog = false
                                }
                                .padding(vertical = 12.dp, horizontal = 8.dp),
                            style = MaterialTheme.typography.bodyLarge
                        )
                    }
                }
            },
            confirmButton = {
                TextButton(onClick = { showOutputDialog = false }) {
                    Text(labelText(LabelKey.ACTION_CANCEL))
                }
            }
        )
    }

    if (showShareDialog) {
        val shareUrl = "https://tremenz.com/"
        val linkCopiedMsg = labelText(LabelKey.SHARE_COPIED)
        AlertDialog(
            onDismissRequest = { showShareDialog = false },
            title = {
                Text(
                    text = labelText(LabelKey.SHARE_TITLE),
                    style = MaterialTheme.typography.headlineSmall
                )
            },
            text = {
                Column {
                    Text(
                        text = labelText(LabelKey.SHARE_MESSAGE),
                        color = scheme.onSurfaceVariant,
                        style = MaterialTheme.typography.bodyMedium
                    )
                    Spacer(modifier = Modifier.height(12.dp))
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(scheme.surfaceVariant, MaterialTheme.shapes.small)
                            .padding(12.dp)
                    ) {
                        Text(
                            text = shareUrl,
                            color = scheme.primary,
                            style = MaterialTheme.typography.bodyMedium,
                            fontWeight = FontWeight.SemiBold,
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }
            },
            confirmButton = {
                TextButton(
                    onClick = {
                        clipboardManager.setText(AnnotatedString(shareUrl))
                        showShareDialog = false
                        showTransient(linkCopiedMsg)
                    }
                ) {
                    Text(labelText(LabelKey.ACTION_COPY))
                }
            },
            dismissButton = {
                TextButton(onClick = { showShareDialog = false }) {
                    Text(labelText(LabelKey.ACTION_CLOSE))
                }
            }
        )
    }


    // The latest arguments, read by the movable player below so it never draws stale captures.
    val latestPlayer = rememberUpdatedState<@Composable (Modifier) -> Unit> { playerModifier ->
        ContainerPlayer(
            mediaItem = currentMedia,
            player = player,
            isPreparingVideo = isPreparingVideo,
            isVideoPlaying = isVideoPlaying,
            onVideoPlayToggle = { play ->
                if (play) {
                    isTabExpanded = false
                }
                onVideoPlayToggle(play)
            },
            isPreparingSong = isPreparingSong,
            isSongPlaying = isSongPlaying,
            onSongStateChange = onSongStateChange,
            onSongError = onSongErrorShown,
            onSongEnding = onSongEnding,
            onSongAdDetected = onSongAdDetected,
            onStop = onStopPlayback,
            fsNotification = fsNotification,
            modifier = playerModifier
        )
    }
    // One player for both layouts: rotating moves the embed and its state instead of remounting them.
    val playerContent = remember {
        movableContentOf { playerModifier: Modifier -> latestPlayer.value(playerModifier) }
    }

    // Rotating a phone is a request for full screen playback. A browser window is landscape almost
    // always, so the Web shell keeps its navigation instead — see isImmersiveLandscape().
    if (isLandscape() && isImmersiveLandscape()) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(Color.Black)
        ) {
            playerContent(Modifier.fillMaxSize())
        }
        return
    }

    val onDestinationClick: (RouteKey) -> Unit = { destination ->
        isTabExpanded = nextTabExpanded(
            canExpand = canExpandTabs,
            isExpanded = isTabExpanded,
            isSameTab = route == destination
        )
        routeName = destination.name
    }

    WindowProvider { windowClass ->
        Row(modifier = Modifier.fillMaxSize()) {
            Scaffold(
                modifier = Modifier.weight(1f),
                topBar = {
                    CenterAlignedTopAppBar(
                        navigationIcon = {
                            IconButton(
                                onClick = { showShareDialog = true }
                            ) {
                                Icon(
                                    painter = iconPainter(IconKey.TREMENZ),
                                    contentDescription = labelText(LabelKey.ACTION_SHARE),
                                    tint = themeSwitch.onToolbarColor,
                                    modifier = Modifier.size(28.dp)
                                )
                            }
                        },
                        title = {
                            Icon(
                                painter = iconPainter(IconKey.WORDMARK),
                                contentDescription = "Tremenz",
                                tint = themeSwitch.wordmarkColor,
                                // Horizontal padding keeps the wordmark off the leading and trailing buttons;
                                // the height cap lets it shrink to the slot width on small screens.
                                modifier = Modifier
                                    .padding(horizontal = 12.dp, vertical = 4.dp)
                                    .heightIn(max = 56.dp)
                                    .aspectRatio(712.8f / 126.44f)
                            )
                        },
                        colors = TopAppBarDefaults.topAppBarColors(
                            containerColor = themeSwitch.toolbarColor,
                            navigationIconContentColor = themeSwitch.onToolbarColor,
                            actionIconContentColor = themeSwitch.onToolbarColor
                        ),
                        actions = {
                            val onThemeToggle = themeSwitch.onToggle
                            if (onThemeToggle != null) {
                                IconButton(onClick = onThemeToggle) {
                                    Icon(
                                        painter = iconPainter(if (themeSwitch.isNight) IconKey.DAY else IconKey.NIGHT),
                                        contentDescription = labelText(
                                            if (themeSwitch.isNight) LabelKey.THEME_DAY else LabelKey.THEME_NIGHT
                                        )
                                    )
                                }
                            }

                            MediaCast(
                                isInfoOpen = isInfoOpen,
                                onInfoToggle = { isInfoOpen = !isInfoOpen },
                                modifier = Modifier
                                    .padding(horizontal = 8.dp)
                                    .size(24.dp)
                            )

                            // Audio Output Selection Menu
                            if (currentMedia is ModelData.Audio && outputs.isNotEmpty()) {
                                IconButton(onClick = { showOutputDialog = true }) {
                                    Icon(
                                        painter = iconPainter(IconKey.OUTPUT),
                                        contentDescription = labelText(LabelKey.OUTPUT_TITLE),
                                        tint = themeSwitch.onToolbarColor
                                    )
                                }
                            }
                        }
                    )
                },
                bottomBar = {
                    if (!isInfoOpen && routes.size > 1) {
                        NavigationBar(
                            containerColor = scheme.surfaceContainerHigh,
                            contentColor = scheme.primary
                        ) {
                            routes.forEach { destination ->
                                val title = labelText(destination.label)
                                NavigationBarItem(
                                    selected = route == destination,
                                    onClick = { onDestinationClick(destination) },
                                    icon = {
                                        Icon(
                                            painter = iconPainter(destination.icon),
                                            contentDescription = title
                                        )
                                    },
                                    label = {
                                        Text(
                                            text = title,
                                            fontWeight = FontWeight.Bold
                                        )
                                    },
                                    colors = NavigationBarItemDefaults.colors(
                                        selectedIconColor = scheme.primary,
                                        selectedTextColor = scheme.primary,
                                        indicatorColor = scheme.primary.copy(alpha = 0.2f),
                                        unselectedIconColor = scheme.onSurfaceVariant,
                                        unselectedTextColor = scheme.onSurfaceVariant
                                    )
                                )
                            }
                        }
                    }
                },
                snackbarHost = {
                    SnackbarHost(hostState = snackbarHostState) { data ->
                        Snackbar(
                            snackbarData = data,
                            containerColor = scheme.surfaceVariant,
                            contentColor = scheme.onSurface,
                            actionColor = scheme.primary
                        )
                    }
                }
            ) { innerPadding ->
                BoxWithConstraints(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(innerPadding)
                        .background(scheme.background)
                ) {
                    val hasPlayer = !isInfoOpen && (route.hasPlayer || currentMedia != null)
                    val availableTabHeight = tabContentHeight(maxWidth, maxHeight, hasPlayer)
                    val isConstrained = isTabConstrained(availableTabHeight, hasPlayer)

                    // Synced for the navigation bar, which is outside this measure; drawing below
                    // reads isConstrained directly.
                    SideEffect {
                        if (canExpandTabs != isConstrained) {
                            canExpandTabs = isConstrained
                            if (!isConstrained) {
                                isTabExpanded = false
                            }
                        }
                    }

                    val actualPlayerHeight = playerHeight(maxWidth, hasPlayer, maxHeight)

                    // The expressive spring overshoots its target, which dips below zero when the overlay
                    // settles flush with the top, and padding rejects negative values.
                    val animatedTopPadding by animateDpAsState(
                        targetValue = tabTopPadding(
                            playerHeight = actualPlayerHeight,
                            // Read from this frame's measure, not the synced flags, so crossing the
                            // threshold never draws one frame with the previous state.
                            isExpanded = hasPlayer && isTabExpanded && isConstrained,
                            isConstrained = isConstrained,
                            collapsedPadding = maxHeight
                        ),
                        animationSpec = MotionSpring.spatial(isReducedMotion()),
                        label = "TabTopPadding"
                    )
                    val topPadding = animatedTopPadding.coerceAtLeast(0.dp)

                    val playAndCollapse: (ModelData.Media) -> Unit = { media ->
                        isTabExpanded = false
                        onPlayMedia(media)
                    }

                    var lastMedia by remember { mutableStateOf(currentMedia) }
                    LaunchedEffect(currentMedia) {
                        if (currentMedia != null && currentMedia != lastMedia) {
                            lastMedia = currentMedia
                            isTabExpanded = false
                        }
                    }

                    LaunchedEffect(isVideoPlaying) {
                        if (isVideoPlaying && isTabExpanded) {
                            isTabExpanded = false
                        }
                    }

                    LaunchedEffect(isSongPlaying) {
                        if (isSongPlaying && isTabExpanded) {
                            isTabExpanded = false
                        }
                    }

                    val screens: @Composable () -> Unit = {
                        if (isInfoOpen) {
                            ScreenSite(
                                onOpenLink = onOpenLink,
                                isConstrained = false,
                                isExpanded = isTabExpanded,
                                onExpandedChange = { isTabExpanded = it },
                                modifier = Modifier.fillMaxSize()
                            )
                        } else {
                            when (route) {
                                RouteKey.MIXES -> ScreenLibrary(
                                    currentMedia = currentMedia,
                                    isVideoPlaying = isVideoPlaying,
                                    isPreparingVideo = isPreparingVideo,
                                    onPlayMedia = playAndCollapse,
                                    onOpenLink = onOpenLink,
                                    modifier = Modifier.fillMaxSize()
                                )

                                RouteKey.PLAYLISTS -> ScreenTracks(
                                    currentMedia = currentMedia,
                                    isSongPlaying = isSongPlaying,
                                    isPreparingSong = isPreparingSong,
                                    isContinuousMix = isContinuousMix,
                                    onContinuousMixToggle = onContinuousMixToggle,
                                    onPlayMedia = playAndCollapse,
                                    modifier = Modifier.fillMaxSize()
                                )

                                RouteKey.RADIO -> ScreenChannels(
                                    currentMedia = currentMedia,
                                    isSongPlaying = isSongPlaying,
                                    isPreparingSong = isPreparingSong,
                                    onPlayMedia = playAndCollapse,
                                    modifier = Modifier.fillMaxSize()
                                )
                            }
                        }
                    }

                    // 16:9 Video/Audio Player container. It stays mounted while browsing a page-only
                    // destination so that navigating away does not tear down playback.
                    if (hasPlayer) {
                        CompositionLocalProvider(
                            LocalPlayerOverlayTop provides if (isConstrained) topPadding else null
                        ) {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(actualPlayerHeight)
                                    .background(scheme.background),
                                contentAlignment = Alignment.Center
                            ) {
                                playerContent(Modifier.fillMaxHeight().aspectRatio(16f / 9f))
                            }
                        }
                    }

                    // Drawn over the player, opaque, and inset from the top by everything the player
                    // takes until a tab click asks for the whole box. The player underneath is
                    // untouched either way: same constraints, same composition, no remount.
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(top = topPadding)
                            .background(scheme.background)
                    ) {
                        screens()
                    }
                }
            }
        }
    }
}

